// ===========================================
// HOOKS: Etiquetas de cliente (CORE) — ORR-364
// ===========================================

import { useCallback } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth0 } from '@auth0/auth0-react'
import { toast } from 'sonner'
import { useSupabase } from '@/lib/supabase-context'
import { useOrganizacion } from '@/core/providers/OrganizacionProvider'
import { usePermissions } from '@/hooks/usePermissions'
import { assertPermiso, handleMutationError } from '@/lib/permisos-guard'
import {
  etiquetasClienteService,
  type EtiquetaCliente,
  type ResultadoEnLote,
} from '../services/etiquetasClienteService'
import { useNotificacionesModulo } from './useNotificacionesModulo'
import { rutaFichaCliente } from '@/lib/ruta-cliente'

export const etiquetasClienteKeys = {
  all: ['etiquetas-cliente'] as const,
  catalogo: (organizacionId?: string) =>
    [...etiquetasClienteKeys.all, 'catalogo', organizacionId] as const,
  deCliente: (clienteId?: string) =>
    [...etiquetasClienteKeys.all, 'de-cliente', clienteId] as const,
  deOrganizacion: (organizacionId?: string) =>
    [...etiquetasClienteKeys.all, 'de-organizacion', organizacionId] as const,
  usos: (etiquetaId?: string) => [...etiquetasClienteKeys.all, 'usos', etiquetaId] as const,
}

/** Mensaje para una etiqueta que no se puede borrar porque una tarea que se repite la usa. */
export const mensajeEtiquetaEnUso = (repeticiones: number) =>
  repeticiones === 1
    ? 'La usa 1 tarea que se repite como grupo. Cambiale el grupo o borrala antes de eliminar la etiqueta.'
    : `La usan ${repeticiones} tareas que se repiten como grupo. Cambiales el grupo o borralas antes de eliminar la etiqueta.`

/** Catálogo de etiquetas de la organización. */
export function useEtiquetasCliente() {
  const { supabase } = useSupabase()
  const { organizacionId } = useOrganizacion()

  return useQuery({
    queryKey: etiquetasClienteKeys.catalogo(organizacionId ?? undefined),
    queryFn: () => etiquetasClienteService.listar(supabase!, organizacionId!),
    enabled: !!supabase && !!organizacionId,
  })
}

/** Ids de las etiquetas que lleva un cliente. */
export function useEtiquetasDeCliente(clienteId: string | undefined) {
  const { supabase } = useSupabase()

  return useQuery({
    queryKey: etiquetasClienteKeys.deCliente(clienteId),
    queryFn: () => etiquetasClienteService.listarIdsDeCliente(supabase!, clienteId!),
    enabled: !!supabase && !!clienteId,
  })
}

/**
 * Etiquetas de TODOS los clientes de la organización, en una sola consulta.
 * Alimenta la columna y el filtro de la base de clientes.
 */
export function useEtiquetasDeClientes() {
  const { supabase } = useSupabase()
  const { organizacionId } = useOrganizacion()

  return useQuery({
    queryKey: etiquetasClienteKeys.deOrganizacion(organizacionId ?? undefined),
    queryFn: () => etiquetasClienteService.listarPorOrganizacion(supabase!, organizacionId!),
    enabled: !!supabase && !!organizacionId,
  })
}

export function useCrearEtiqueta() {
  const { supabase } = useSupabase()
  const { organizacionId } = useOrganizacion()
  const { user } = useAuth0()
  const { puede } = usePermissions()
  const queryClient = useQueryClient()

  return useMutation({
    // Una etiqueta es un atributo del cliente, no una entidad propia: usa el permiso de cliente en
    // vez de inventar uno nuevo.
    mutationFn: (nombre: string) => {
      assertPermiso(puede, 'editarCliente')
      return etiquetasClienteService.crear(supabase!, {
        organizacionId: organizacionId!,
        nombre,
        createdBy: user?.email ?? '',
      })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: etiquetasClienteKeys.all })
    },
    onError: (error) => {
      // 23505 contra el índice único case-insensitive: el usuario escribió una que ya existe.
      if ((error as { code?: string }).code === '23505') {
        toast.error('Ya existe una etiqueta con ese nombre')
        return
      }
      handleMutationError(error, 'No se pudo crear la etiqueta')
    },
  })
}

/** Clientes y tareas que se repiten que usan una etiqueta. Consulta solo con una etiqueta elegida. */
export function useUsosEtiqueta(etiquetaId: string | undefined) {
  const { supabase } = useSupabase()
  const { organizacionId } = useOrganizacion()

  return useQuery({
    queryKey: etiquetasClienteKeys.usos(etiquetaId),
    queryFn: () => etiquetasClienteService.contarUsos(supabase!, organizacionId!, etiquetaId!),
    enabled: !!supabase && !!organizacionId && !!etiquetaId,
    staleTime: 0,
  })
}

export function useEliminarEtiqueta() {
  const { supabase } = useSupabase()
  const { organizacionId } = useOrganizacion()
  const { puede } = usePermissions()
  const queryClient = useQueryClient()

  return useMutation({
    // Mismo permiso que crear: la etiqueta es un atributo del cliente.
    mutationFn: (etiquetaId: string) => {
      assertPermiso(puede, 'editarCliente')
      return etiquetasClienteService.eliminar(supabase!, organizacionId!, etiquetaId)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: etiquetasClienteKeys.all })
      toast.success('Etiqueta eliminada')
    },
    onError: (error) => {
      // 23503: la clave foránea de una tarea que se repite impide el borrado. Pasa cuando el
      // usuario no ve esa repetición y el conteo previo dio 0.
      if ((error as { code?: string }).code === '23503') {
        toast.error('No se puede eliminar la etiqueta', {
          description: 'Una tarea que se repite la usa como grupo. Cambiale el grupo o borrala antes.',
        })
        return
      }
      handleMutationError(error, 'No se pudo eliminar la etiqueta')
    },
  })
}

export function useAsignarEtiqueta() {
  const { supabase } = useSupabase()
  const { organizacionId } = useOrganizacion()
  const { user } = useAuth0()
  const { puede } = usePermissions()
  const queryClient = useQueryClient()
  const { notificar, responsablesCliente } = useNotificacionesModulo()

  return useMutation({
    mutationFn: ({ clienteId, etiquetaId }: EtiquetaDeCliente) => {
      assertPermiso(puede, 'editarCliente')
      return etiquetasClienteService.asignar(supabase!, {
        organizacionId: organizacionId!,
        clienteId,
        etiquetaId,
        createdBy: user?.email ?? '',
      })
    },
    onSuccess: async (_, { clienteId, clienteNombre, etiquetaNombre }) => {
      // `all` incluye `deOrganizacion`: sin esto, asignar desde la card de la ficha deja la
      // columna y el filtro de la base de clientes con datos viejos hasta que venzan solos.
      void queryClient.invalidateQueries({ queryKey: etiquetasClienteKeys.all })

      // Etiquetar no es cosmético: enrola al cliente en las tareas que esa etiqueta genere sola.
      // El responsable tiene que enterarse. Best-effort, igual que `useAsignarImpuesto` (ORR-56).
      if (!clienteNombre || !etiquetaNombre) return
      try {
        const destinatarios = await responsablesCliente(clienteId)
        if (destinatarios.length === 0) return
        const actor = user?.name || user?.email || 'Alguien'
        await notificar({
          tipo: 'cliente',
          titulo: 'Etiqueta asignada',
          mensaje: `${actor} etiquetó a ${clienteNombre} como "${etiquetaNombre}"`,
          enlace: rutaFichaCliente(clienteId),
          destinatarios,
        })
      } catch (err) {
        console.error('[notificaciones] error notificando etiqueta asignada:', err)
      }
    },
    onError: (error) => handleMutationError(error, 'No se pudo asignar la etiqueta'),
  })
}

export function useDesasignarEtiqueta() {
  const { supabase } = useSupabase()
  const { puede } = usePermissions()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ clienteId, etiquetaId }: EtiquetaDeCliente) => {
      assertPermiso(puede, 'editarCliente')
      return etiquetasClienteService.desasignar(supabase!, clienteId, etiquetaId)
    },
    onSuccess: (_, { clienteId }) => {
      void queryClient.invalidateQueries({ queryKey: etiquetasClienteKeys.deCliente(clienteId) })
      void queryClient.invalidateQueries({ queryKey: etiquetasClienteKeys.all })
    },
    onError: (error) => handleMutationError(error, 'No se pudo quitar la etiqueta'),
  })
}

/**
 * Lleva las etiquetas de un cliente al conjunto elegido en el alta o la edición. Un fallo
 * parcial no revierte nada: el caller recibe el conteo y avisa, sin relanzar — la mutación
 * del cliente ya se hizo y no depende de esto para considerarse exitosa.
 */
export function useReconciliarEtiquetasCliente() {
  const { supabase } = useSupabase()
  const { organizacionId } = useOrganizacion()
  const { user } = useAuth0()
  const { puede } = usePermissions()
  const queryClient = useQueryClient()
  const { notificar, responsablesCliente } = useNotificacionesModulo()

  return useMutation({
    mutationFn: (input: ReconciliarEtiquetasInput) => {
      assertPermiso(puede, 'editarCliente')
      return etiquetasClienteService.reconciliar(supabase!, {
        organizacionId: organizacionId!,
        clienteId: input.clienteId,
        idsActuales: input.idsActuales,
        idsFinal: input.idsFinal,
        createdBy: user?.email ?? '',
      })
    },
    onSuccess: async ({ asignadas }, input) => {
      void queryClient.invalidateQueries({ queryKey: etiquetasClienteKeys.all })

      // Una sola notificación resumen por guardado, no una por etiqueta: coherente con que
      // `useDesasignarEtiqueta` tampoco notifica al quitar.
      if (asignadas.length === 0 || !input.clienteNombre || !input.nombresPorId) return
      try {
        const destinatarios = await responsablesCliente(input.clienteId)
        if (destinatarios.length === 0) return
        const nombres = asignadas.map((id) => input.nombresPorId?.get(id)).filter((n): n is string => !!n)
        if (nombres.length === 0) return
        const actor = user?.name || user?.email || 'Alguien'
        await notificar({
          tipo: 'cliente',
          titulo: 'Etiqueta asignada',
          mensaje: `${actor} etiquetó a ${input.clienteNombre} como "${nombres.join('", "')}"`,
          enlace: rutaFichaCliente(input.clienteId),
          destinatarios,
        })
      } catch (err) {
        console.error('[notificaciones] error notificando etiquetas asignadas:', err)
      }
    },
    // Sin onError con toast: el caller (alta/edición de cliente) decide cómo avisar del fallo,
    // porque el cliente ya se guardó y el mensaje genérico de mutación sería engañoso acá.
  })
}

/** Lee en el momento cuántas tareas que se repiten usan una etiqueta como grupo. */
export function useContarRepeticionesEtiqueta() {
  const { supabase } = useSupabase()
  const { organizacionId } = useOrganizacion()

  return useCallback(
    async (etiquetaId: string) => {
      const { repeticiones } = await etiquetasClienteService.contarUsos(
        supabase!,
        organizacionId!,
        etiquetaId
      )
      return repeticiones
    },
    [supabase, organizacionId]
  )
}

/** Clientes de la lista que tienen la etiqueta según el mapa de la base de clientes. */
const clientesConEtiqueta = (
  mapa: Map<string, string[]>,
  clienteIds: string[],
  etiquetaId: string
) => new Set(clienteIds.filter((id) => mapa.get(id)?.includes(etiquetaId)))

/**
 * Pone una etiqueta a los clientes elegidos. Saltea a los que ya la tienen según la base de
 * clientes en caché, y manda una sola notificación a los responsables de los etiquetados.
 */
export function useEtiquetarEnLote() {
  const { supabase } = useSupabase()
  const { organizacionId } = useOrganizacion()
  const { user } = useAuth0()
  const { puede } = usePermissions()
  const queryClient = useQueryClient()
  const { notificar, responsablesCliente } = useNotificacionesModulo()

  return useMutation({
    mutationFn: async ({ clienteIds, etiqueta }: EtiquetaEnLote): Promise<ResultadoEtiquetaEnLote> => {
      assertPermiso(puede, 'editarCliente')
      const mapa = queryClient.getQueryData<Map<string, string[]>>(
        etiquetasClienteKeys.deOrganizacion(organizacionId ?? undefined)
      )
      const yaLaTienen = mapa ? clientesConEtiqueta(mapa, clienteIds, etiqueta.id) : new Set<string>()
      const resultado = await etiquetasClienteService.asignarEnLote(supabase!, {
        organizacionId: organizacionId!,
        clienteIds: clienteIds.filter((id) => !yaLaTienen.has(id)),
        etiquetaId: etiqueta.id,
        createdBy: user?.email ?? '',
      })
      return { ...resultado, sinCambio: yaLaTienen.size }
    },
    onSuccess: async ({ hechos }, { etiqueta }) => {
      void queryClient.invalidateQueries({ queryKey: etiquetasClienteKeys.all })

      if (hechos.length === 0) return
      try {
        const listas = await Promise.all(hechos.map((id) => responsablesCliente(id)))
        const destinatarios = Array.from(new Set(listas.flat()))
        if (destinatarios.length === 0) return
        const actor = user?.name || user?.email || 'Alguien'
        const clientes = hechos.length === 1 ? 'a 1 cliente' : `${hechos.length} clientes`
        await notificar({
          tipo: 'cliente',
          titulo: 'Etiqueta asignada',
          mensaje: `${actor} etiquetó ${clientes} como "${etiqueta.nombre}"`,
          enlace: '/clientes',
          destinatarios,
        })
      } catch (err) {
        console.error('[notificaciones] error notificando etiquetas en lote:', err)
      }
    },
    onError: (error) => handleMutationError(error, 'No se pudo etiquetar a los clientes'),
  })
}

/** Quita una etiqueta a los clientes elegidos que la tienen. No notifica, igual que de a uno. */
export function useQuitarEtiquetaEnLote() {
  const { supabase } = useSupabase()
  const { organizacionId } = useOrganizacion()
  const { puede } = usePermissions()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ clienteIds, etiqueta }: EtiquetaEnLote): Promise<ResultadoEtiquetaEnLote> => {
      assertPermiso(puede, 'editarCliente')
      const mapa = queryClient.getQueryData<Map<string, string[]>>(
        etiquetasClienteKeys.deOrganizacion(organizacionId ?? undefined)
      )
      const laTienen = mapa ? clientesConEtiqueta(mapa, clienteIds, etiqueta.id) : new Set(clienteIds)
      const resultado = await etiquetasClienteService.quitarEnLote(supabase!, {
        clienteIds: Array.from(laTienen),
        etiquetaId: etiqueta.id,
      })
      return { ...resultado, sinCambio: clienteIds.length - laTienen.size }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: etiquetasClienteKeys.all })
    },
    onError: (error) => handleMutationError(error, 'No se pudo quitar la etiqueta a los clientes'),
  })
}

interface EtiquetaEnLote {
  clienteIds: string[]
  etiqueta: Pick<EtiquetaCliente, 'id' | 'nombre'>
}

/** `sinCambio` cuenta los clientes que ya estaban como se pedía y no se tocaron. */
export interface ResultadoEtiquetaEnLote extends ResultadoEnLote {
  sinCambio: number
}

interface ReconciliarEtiquetasInput {
  clienteId: string
  idsActuales: string[]
  idsFinal: string[]
  /** Solo para el mensaje de la notificación; sin ellos la reconciliación funciona igual. */
  clienteNombre?: string
  nombresPorId?: Map<string, string>
}

interface EtiquetaDeCliente {
  clienteId: string
  etiquetaId: string
  /** Solo para el mensaje de la notificación; sin ellos la mutación funciona igual. */
  clienteNombre?: string
  etiquetaNombre?: string
}
